import React, { useEffect, useMemo, useRef, useState,useCallback } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "./Locations/globals.css";
import { api, publicCatalogRequest } from "../utils/api";
import { getMapDefaultCenter, getMapGeocodeCountry } from "../utils/p1Region";
import SearchFilterBar from "./Locations/SearchFilterBar";
import MerchantInfoWindow from "./Locations/MerchantInfoWindow";
import customMarkerIcon from "./Locations/icon-washclub.png";
import {
  accessUsersLocation,
  getAllAvailableProducts,
  getMerchantStoresWithProducts,
  getMerchantsWithCoordinates,
  groupMerchantStoresByAccount,
  loadGoogleMapsApi,
  matchMerchantProducts,
  matchSearchToMerchant
} from "./Locations/utils";

const DEFAULT_MAP_CENTER = getMapDefaultCenter();
const SEARCH_RADIUS_KM = 50;
const NEAREST_FALLBACK_COUNT = 10;

const logMapLoad = (label, value) => {
  console.log(`[P1 Map load] ${label}`, value);
};

const toLeafletLatLng = (location) => {
  if (!location) return null;

  if (Array.isArray(location) && location.length >= 2) {
    return { lat: Number(location[0]), lng: Number(location[1]) };
  }

  if (typeof location.lat === "function" && typeof location.lng === "function") {
    return { lat: location.lat(), lng: location.lng() };
  }

  if (typeof location.lat === "number" && typeof location.lng === "number") {
    return { lat: location.lat, lng: location.lng };
  }

  return null;
};

const merchantLatLng = (merchant) => toLeafletLatLng(merchant?.coordinates);

const directionsUrl = ({ coordinates, name, address }) => {
  const destination = coordinates ? `${coordinates.lat},${coordinates.lng}` : `${name || ""} ${address || ""}`;
  return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(destination)}`;
};

const getReadableAddress = (merchant) => (merchant?.address ? Object.values(merchant.address).join(", ") : "Address unavailable");

const getDistanceInKm = (from, to) => {
  if (!from || !to || !Number.isFinite(from.lat) || !Number.isFinite(from.lng) || !Number.isFinite(to.lat) || !Number.isFinite(to.lng)) {
    return Infinity;
  }

  const earthRadius = 6371;
  const dLat = ((to.lat - from.lat) * Math.PI) / 180;
  const dLng = ((to.lng - from.lng) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((from.lat * Math.PI) / 180) * Math.cos((to.lat * Math.PI) / 180) * Math.sin(dLng / 2) * Math.sin(dLng / 2);

  return 2 * earthRadius * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

const withDistanceFrom = (merchants, origin) =>
  merchants
    .map((merchant) => ({
      ...merchant,
      distance: getDistanceInKm(origin, merchantLatLng(merchant))
    }))
    .sort((a, b) => (a.distance || Infinity) - (b.distance || Infinity));

const nearbyOrFallback = (merchants, origin) => {
  const ranked = withDistanceFrom(merchants, origin);
  const withinRadius = ranked.filter((merchant) => merchant.distance <= SEARCH_RADIUS_KM);
  return withinRadius.length > 0 ? withinRadius : ranked.slice(0, NEAREST_FALLBACK_COUNT);
};

const distanceText = (location) => {
  if (typeof location.distance !== "number" || !Number.isFinite(location.distance)) return "View location";
  return location.distance < 1 ? `${Math.round(location.distance * 1000)} m away` : `${location.distance.toFixed(1)} km away`;
};

function markerIcon(active = false) {
  return L.divIcon({
    className: "",
    html: `<div class="custom-marker${active ? " active" : ""}">🚗</div>`,
    iconSize: [42, 42],
    iconAnchor: [21, 21],
    popupAnchor: [0, -24]
  });
}

function userMarkerIcon() {
  return L.divIcon({
    className: "",
    html: '<div class="user-marker"></div>',
    iconSize: [17, 17],
    iconAnchor: [8.5, 8.5]
  });
}

function popupHtml(location) {
  return `
    <div>
      <div class="popup-eyebrow">PRIORITY ONE</div>
      <div class="popup-title">${location.name}</div>
      <div class="popup-address">${getReadableAddress(location)}</div>
      <a class="popup-directions" href="${directionsUrl(location)}" target="_blank" rel="noopener noreferrer">
        <span>Get Directions</span><span>→</span>
      </a>
    </div>
  `;
}

const CurrentWashLocationsMap = () => {
  const mapElement = useRef(null);
  const mapRef = useRef(null);
  const markerLayerRef = useRef(null);
  const userMarkerRef = useRef(null);
  const searchMarkerRef = useRef(null);
  const searchInputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const [merchants, setMerchants] = useState([]);
  const [storesLoaded, setStoresLoaded] = useState(false);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);
  const [nearby, setNearby] = useState(false);
  const [userCoords, setUserCoords] = useState(null);
  const [error, setError] = useState("");
  const [mobileView, setMobileView] = useState("map");
  const [filteredProducts, setFilteredProducts] = useState([]);

  const [zoom, setZoom] = useState(9);
  const [center, setCenter] = useState(DEFAULT_MAP_CENTER);

  const [openStore, setOpenStore] = useState("");
  const [searchValue, setSearchValue] = useState(""); // now purely "suburb/postcode" query text

  const [map, setMap] = useState(null);
  const [searchedLocation, setSearchedLocation] = useState(null);
  const [searchBounds, setSearchBounds] = useState(null);

  const detectUserLocationByIp = useCallback(async () => {
    try {
      const response = await fetch("https://ipapi.co/json/");
      if (!response.ok) throw new Error("IP lookup failed");

      const data = await response.json();
      const latitude = Number(data?.latitude);
      const longitude = Number(data?.longitude);

      logMapLoad("IP lookup response", {
        city: data?.city,
        region: data?.region,
        country: data?.country_name,
        latitude,
        longitude
      });

      if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
        logMapLoad("IP lookup ignored — invalid coordinates", { latitude, longitude });
        return;
      }

      const coords = [latitude, longitude];
      setUserCoords((current) => {
        const next = current ?? coords;
        logMapLoad("setUserCoords from IP", { incoming: coords, alreadySet: current, using: next, nearby: true });
        return next;
      });
      setNearby(true);
      if (mapRef.current) {
        mapRef.current.setView([latitude, longitude], 9, { duration: 0.7 });
        logMapLoad("map.setView from IP", { lat: latitude, lng: longitude, zoom: 9 });
      }
    } catch (error) {
      console.log("[P1 Map load] Unable to detect user location from IP — list will use default city:", error);
    }
  }, []);

  const toggleOpen = (id) => (openStore === id ? setOpenStore("") : setOpenStore(id));
  const closeStore = () => setOpenStore("");

  /** Not used for now. We'd like to group merchant stores by merchant account by customising the Marker on the 
      map, e.g. all merchant stores owned by Merchant X will have a green color, and so on. */
  const merchantStoresByAccount = merchants && groupMerchantStoresByAccount(merchants);
  const allProducts = useMemo(() => getAllAvailableProducts(merchants), [merchants]);

  const filtered = useMemo(() => {
    let result = merchants.filter((merchant) => {
      if (!merchant || !merchant.name) return false;
      return filteredProducts.length === 0 || matchMerchantProducts(merchant, filteredProducts);
    });

    const originFromUser = userCoords
      ? { lat: Number(userCoords[0]), lng: Number(userCoords[1]) }
      : null;
    const origin = searchedLocation || originFromUser || DEFAULT_MAP_CENTER;
    const originSource = searchedLocation ? "search" : originFromUser ? "gps-or-ip" : "default-city";

    const distanceFiltered = nearbyOrFallback(result, origin);
    logMapLoad("filtered by distance", {
      originSource,
      origin,
      merchantCount: result.length,
      shownCount: distanceFiltered.length,
      radiusKm: SEARCH_RADIUS_KM,
      shown: distanceFiltered.slice(0, 5).map((merchant) => ({
        name: merchant.name,
        distanceKm: merchant.distance
      }))
    });
    return distanceFiltered;
  }, [merchants, userCoords, filteredProducts, searchedLocation]);

  useEffect(() => {
    logMapLoad("page init", {
      defaultMapCenter: DEFAULT_MAP_CENTER,
      searchRadiusKm: SEARCH_RADIUS_KM,
      nearestFallbackCount: NEAREST_FALLBACK_COUNT,
      geocodeCountry: getMapGeocodeCountry(),
      href: typeof window !== "undefined" ? window.location.href : null
    });

    accessUsersLocation(
      (position) => {
        const coords = [position.coords.latitude, position.coords.longitude];
        logMapLoad("GPS success — setUserCoords + setNearby(true)", {
          coords,
          accuracy: position.coords.accuracy
        });
        setUserCoords(coords);
        setNearby(true);
        if (mapRef.current) {
          mapRef.current.flyTo(coords, 9, { duration: 0.8 });
          logMapLoad("map.flyTo from GPS", { coords, zoom: 9 });
        }
      },
      (geoError) => {
        logMapLoad("GPS denied/failed — using IP fallback", {
          code: geoError?.code,
          message: geoError?.message
        });
        detectUserLocationByIp();
      }
    );

    const getMerchantStores = async () => {
      try {
        const response = await api("v1/merchantStore", "GET", null, publicCatalogRequest);
        const allStores = response?.data?.data?.allMerchantStores || [];
        logMapLoad("merchantStore API", {
          success: response?.data?.success,
          storeCount: allStores.length
        });
        if (response?.data?.success) {
          const merchantsWithCoordinates = await getMerchantsWithCoordinates(allStores);
          const merchantsWithProducts = await getMerchantStoresWithProducts(merchantsWithCoordinates);
          logMapLoad("setMerchants", {
            withCoordinates: merchantsWithCoordinates.length,
            names: merchantsWithProducts.map((merchant) => merchant.name)
          });
          setMerchants(merchantsWithProducts);
        }
      } catch (error) {
        console.error("[P1 Map load] Unable to load merchant stores:", error);
      } finally {
        logMapLoad("setStoresLoaded", true);
        setStoresLoaded(true);
      }
    };

    getMerchantStores();
  }, []);

  const panToSuburbOrPostcode = useCallback((searchQuery) => {
    const q = (searchQuery ?? "").trim();
    const map = mapRef.current;
    if (!q || !map || !window.google) return;

    const geocoder = new window.google.maps.Geocoder();

    geocoder.geocode(
      {
        address: q,
        componentRestrictions: { country: getMapGeocodeCountry() }
      },
      (results, status) => {
        if (status !== "OK" || !results?.[0]) {
          console.log("Geocode failed:", status);
          return;
        }

        const result = results[0];
        const loc = toLeafletLatLng(result.geometry?.location);
        if (loc) setSearchedLocation(loc);

        if (result.geometry?.viewport) {
          const sw = toLeafletLatLng(result.geometry.viewport.getSouthWest());
          const ne = toLeafletLatLng(result.geometry.viewport.getNorthEast());
          if (sw && ne) {
            map.fitBounds([
              [sw.lat, sw.lng],
              [ne.lat, ne.lng]
            ]);
            const z = map.getZoom();
            if (z != null) map.setZoom(Math.max(1, z - 1));
            return;
          }
        }

        if (loc) {
          map.panTo([loc.lat, loc.lng]);
          map.setZoom(9);
        }
      }
    );
  }, []);

  const loadGoogleMapsPlaces = useCallback(
    () => loadGoogleMapsApi({ places: true }).then((ready) => ready && Boolean(window.google?.maps?.places)),
    []
  );

  // On load with ?address= (and optional ?name=) from "View Location", pan map to that location (once)
  const hasPannedFromUrl = React.useRef(false);
  useEffect(() => {
    if (!mapElement.current) return;

    const map = L.map(mapElement.current, { zoomControl: false, attributionControl: true }).setView([DEFAULT_MAP_CENTER.lat, DEFAULT_MAP_CENTER.lng], 9);
    logMapLoad("Leaflet map created", { center: DEFAULT_MAP_CENTER, zoom: 9 });
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19,
      attribution: '&copy; <a href="https://www.openstreetmap.org/">OpenStreetMap</a>'
    }).addTo(map);

    markerLayerRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;

    const resize = () => setTimeout(() => map.invalidateSize(), 100);
    window.addEventListener("resize", resize);

    return () => {
      window.removeEventListener("resize", resize);
      map.remove();
    };
  }, []);

  useEffect(() => {
    if (!mapRef.current || userCoords || searchedLocation) return;
    detectUserLocationByIp();
  }, [detectUserLocationByIp, userCoords, searchedLocation]);

  useEffect(() => {
    if (!searchInputRef.current) return;

    let disposed = false;

    loadGoogleMapsPlaces().then((ready) => {
      if (!ready || disposed || !searchInputRef.current || !window.google?.maps?.places) return;
      if (autocompleteRef.current) return;

      const autocomplete = new window.google.maps.places.Autocomplete(searchInputRef.current, {
        types: ["geocode"],
        fields: ["formatted_address", "geometry", "name"],
        componentRestrictions: { country: getMapGeocodeCountry().toLowerCase() }
      });

      autocomplete.addListener("place_changed", () => {
        const place = autocomplete.getPlace();
        const loc = toLeafletLatLng(place?.geometry?.location);
        if (!loc) return;

        const selectedText = place.formatted_address || place.name || "";
        if (selectedText) {
          setQuery(selectedText);
        }
        setSearchedLocation(loc);
        setSelected(null);
        setNearby(false);
        mapRef.current?.panTo([loc.lat, loc.lng]);
        mapRef.current?.setZoom(9);
      });

      autocompleteRef.current = autocomplete;
    });

    return () => {
      disposed = true;
    };
  }, [loadGoogleMapsPlaces]);

  useEffect(() => {
    const map = mapRef.current;
    const layer = markerLayerRef.current;
    if (!map || !layer) return;

    layer.clearLayers();
    filtered.forEach((merchant) => {
      const coords = merchantLatLng(merchant);
      if (!coords) return;

      const marker = L.marker([coords.lat, coords.lng], {
        icon: markerIcon(selected?._id === merchant._id)
      }).addTo(layer);

      marker.bindPopup(popupHtml(merchant), { closeButton: true, offset: [0, -5] });
      marker.on("click", () => selectLocation(merchant, true));
    });
  }, [filtered, selected]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !userCoords) return;

    if (userMarkerRef.current) {
      userMarkerRef.current.setLatLng(userCoords);
      return;
    }

    userMarkerRef.current = L.marker(userCoords, { icon: userMarkerIcon() }).addTo(map).bindPopup("You are here");
  }, [userCoords]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !storesLoaded || filtered.length === 0) return;

    const points = filtered.map(merchantLatLng).filter(Boolean);
    if (userCoords) {
      points.push({ lat: userCoords[0], lng: userCoords[1] });
    } else if (searchedLocation) {
      points.push(searchedLocation);
    } else {
      points.push(DEFAULT_MAP_CENTER);
    }
    if (points.length === 0) return;

    map.fitBounds(
      points.map((point) => [point.lat, point.lng]),
      { padding: [48, 48], maxZoom: 10, animate: true }
    );
  }, [filtered, searchedLocation, storesLoaded, userCoords]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    if (searchMarkerRef.current) {
      searchMarkerRef.current.remove();
      searchMarkerRef.current = null;
    }

    if (!searchedLocation) return;

    const searchMarker = L.circleMarker([searchedLocation.lat, searchedLocation.lng], {
      radius: 12,
      color: "#1d4ed8",
      weight: 3,
      fillColor: "#60a5fa",
      fillOpacity: 0.9
    })
      .bindPopup("Searched location")
      .addTo(map);

    searchMarkerRef.current = searchMarker;
  }, [searchedLocation]);

  function selectLocation(location, moveMap = false) {
    setSelected(location);
    if (moveMap && mapRef.current && location?.coordinates) {
      const coords = merchantLatLng(location);
      if (!coords) return;
      mapRef.current.flyTo([coords.lat, coords.lng], Math.max(mapRef.current.getZoom(), 13), { duration: 0.7 });
      setTimeout(() => {
        markerLayerRef.current?.eachLayer((marker) => {
          const ll = marker.getLatLng();
          if (ll.lat === coords.lat && ll.lng === coords.lng) marker.openPopup();
        });
      }, 450);
    }
  }

  function clearSearch() {
    setQuery("");
    setSearchedLocation(null);
    setSearchBounds(null);
    setSelected(null);
    setNearby(false);
    setError("");
    setFilteredProducts([]);
  }

  function searchLocations() {
    const trimmedQuery = query.trim();
    if (!trimmedQuery) {
      setError("Please enter a suburb or city");
      return;
    }

    setError("");
    setSelected(null);
    setNearby(false);
    setSearchBounds(null);
    panToSuburbOrPostcode(trimmedQuery);
  }

  function locateUser() {
    setError("");
    if (!navigator.geolocation) {
      setError("Location services are not supported by this browser.");
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const coords = [position.coords.latitude, position.coords.longitude];
        setQuery("");
        setSearchedLocation(null);
        setUserCoords(coords);
        setNearby(true);
        if (mapRef.current) {
          mapRef.current.flyTo(coords, 9, { duration: 0.8 });
        }
      },
      () => setError("Unable to access your location. Please allow location access in your browser."),
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    );
  }

  return (
    <div className="priority-map-app">
      <div className="priority-layout">
        <aside className={`priority-sidebar${mobileView === "list" ? " priority-mobile-visible" : ""}`}>
          <div className="priority-sidebar-header">
            <div className="priority-eyebrow">PRIORITY ONE</div>
            <h1>Find a Wash</h1>
          </div>

          <div className="priority-locations-list">
            {!storesLoaded ? (
              <div className="priority-empty">
                <p>Loading locations...</p>
              </div>
            ) : !filtered.length ? (
              <div className="priority-empty">
                <div className="priority-empty-icon">🔍</div>
                <h3>No locations found</h3>
                <p>Try another suburb or city.</p>
                <button onClick={clearSearch}>Clear Search</button>
              </div>
            ) : (
              filtered.map((merchant) => (
                <article
                  key={merchant._id}
                  className={`priority-location-card${selected?._id === merchant._id ? " priority-location-card-active" : ""}`}
                  onClick={(event) => {
                    if (!event.target.closest("a")) selectLocation(merchant, true);
                  }}
                >
                  <h3>{merchant.name}</h3>
                  <p className="priority-address">{getReadableAddress(merchant)}</p>
                  <div className="priority-meta">
                    <span className="priority-distance">{distanceText(merchant)}</span>
                    <span className="priority-status">
                      <span className="priority-status-dot" />
                      Available
                    </span>
                  </div>
                  <a
                    className="priority-directions-btn"
                    href={directionsUrl(merchant)}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(event) => event.stopPropagation()}
                  >
                    <span>Get Directions</span>
                    <span>→</span>
                  </a>
                </article>
              ))
            )}
          </div>
        </aside>

        <main className="priority-map-container">
          <div ref={mapElement} id="priority-map" className="priority-map-surface" />

          <div className="priority-map-controls">
            <div className="priority-search-row">
              <div className="priority-search-box">
                <span className="priority-search-icon">🔍</span>
                <input
                  ref={searchInputRef}
                  value={query}
                  onChange={(event) => {
                    setQuery(event.target.value);
                    setSelected(null);
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" && query.trim()) {
                      event.preventDefault();
                      searchLocations();
                    }
                  }}
                  placeholder="Search suburb or city"
                  autoComplete="off"
                />
                {query && (
                  <button className="priority-clear-btn priority-clear-btn-visible" aria-label="Clear search" onClick={clearSearch}>
                    ×
                  </button>
                )}
              </div>
              <button className="priority-near-me" onClick={searchLocations}>
                <span className="priority-near-icon">🔎</span>
                <span className="priority-near-text">Search</span>
              </button>
              <button className="priority-near-me" onClick={locateUser}>
                <span className="priority-near-icon">⌖</span>
                <span className="priority-near-text">Near Me</span>
              </button>
            </div>
            {error && <div className="priority-error-message priority-error-message-visible">{error}</div>}
          </div>

          <div className={`priority-view-toggle priority-view-toggle-map${mobileView === "map" ? " priority-view-toggle-visible" : ""}`}>
            <button
              className={mobileView === "map" ? "priority-view-toggle-active" : ""}
              onClick={() => {
                setMobileView("map");
                setTimeout(() => mapRef.current?.invalidateSize(), 100);
              }}
            >
              Map
            </button>
            <button className={mobileView === "list" ? "priority-view-toggle-active" : ""} onClick={() => setMobileView("list")}>
              List
            </button>
          </div>

          <div className="priority-map-buttons">
            <button className="priority-map-button" title="Find my location" onClick={locateUser}>⌖</button>
          </div>

          {selected && (
            <div className="priority-mobile-card">
              <div className="priority-mobile-handle" />
              <button className="priority-close-mobile" onClick={() => setSelected(null)}>×</button>
              <div className="priority-popup-eyebrow">PRIORITY ONE</div>
              <h2>{selected.name}</h2>
              <p>{getReadableAddress(selected)}</p>
              <a className="priority-directions-btn" href={directionsUrl(selected)} target="_blank" rel="noopener noreferrer">
                <span>Get Directions</span>
                <span>→</span>
              </a>
            </div>
          )}

        </main>
      </div>
    </div>
  );
};

export default CurrentWashLocationsMap;
